import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Utensils, Soup, Plus, Users, Star, MapPin, X, Check, UserX, CalendarClock,
  ShieldAlert, Ban, Clock, Loader2, PartyPopper, ImagePlus, TrendingUp, Wallet, Sparkles, CalendarDays, Share2, Send, Quote, MessageCircle,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const FEE_NOTE = "Real money, in your local currency. Platform keeps 15% (paid to My Kind via UPI); you receive 85% directly.";
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");
const money = (sym, amt) => `${sym || ""}${Number(amt || 0).toLocaleString()}`;

function Stars({ value = 0, onRate, size = 18, testidPrefix }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={testidPrefix ? `${testidPrefix}-${n}` : undefined}
          disabled={!onRate}
          onClick={() => onRate && onRate(n)}
          className={onRate ? "hover:scale-110 transition-transform" : "cursor-default"}
        >
          <Star size={size} className={n <= Math.round(value) ? "text-brand fill-brand" : "text-sec"} />
        </button>
      ))}
    </span>
  );
}

export default function CommunityBoard({ kind }) {
  const isMeal = kind === "meal";
  const { user, meta, countryObj } = useApp();
  const currencyList = Object.entries(meta?.currencies || {}).map(([code, v]) => ({ code, ...v }));
  const defaultCur = countryObj?.currency || "USD";
  const [list, setList] = useState([]);
  const [types, setTypes] = useState([]);
  const [tab, setTab] = useState("discover"); // discover | mine
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dash, setDash] = useState(null);
  const [coords, setCoords] = useState(null);
  const blank = { type: "", title: "", description: "", whats_included: "", menu: "", artists: "", activities: "", instructions: "", attire: "", couples_only: false, pet_friendly: false, kids_welcome: false, age_group: "", price_per_person: isMeal ? 100 : 200, currency: defaultCur, host_upi: "", capacity: isMeal ? 6 : 20, when: "", start_at: "", address: "", images: [] };
  const [form, setForm] = useState(blank);

  const label = isMeal ? "Community Meals" : "Community Events";
  const HeroIcon = isMeal ? Soup : PartyPopper;

  const loadSeq = useRef(0);
  const load = (which = tab) => {
    const seq = ++loadSeq.current;
    const geo = coords ? `&lat=${coords.lat}&lng=${coords.lng}` : "";
    api.get(`/events?kind=${kind}&mine=${which === "mine"}${geo}`)
      .then(({ data }) => {
        if (seq !== loadSeq.current) return; // ignore out-of-order responses
        setList(data.listings || []);
        setTypes(isMeal ? (data.meal_types || []) : (data.event_categories || []));
      })
      .catch(() => {});
  };
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: +pos.coords.latitude.toFixed(4), lng: +pos.coords.longitude.toFixed(4) }),
      () => {}, { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, []);
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [kind, tab, coords]);
  useEffect(() => { if (tab === "mine" && user) api.get(`/events/host/dashboard?kind=${kind}`).then(({ data }) => setDash(data)).catch(() => {}); else setDash(null); /* eslint-disable-next-line */ }, [tab, kind, user]);
  useEffect(() => { if (types.length && !form.type) setForm((f) => ({ ...f, type: types[0] })); /* eslint-disable-next-line */ }, [types]);
  useEffect(() => { if (openId) refresh(); else setDetail(null); /* eslint-disable-next-line */ }, [openId]);

  const refresh = () => api.get(`/events/${openId}`).then(({ data }) => setDetail(data)).catch(() => {});

  const create = async () => {
    if (!form.title.trim()) { toast.error("Add a title."); return; }
    setBusy(true);
    try {
      await api.post("/events", { ...form, kind, price_per_person: Number(form.price_per_person), capacity: Number(form.capacity) });
      toast.success(`${isMeal ? "Meal" : "Event"} published!`);
      setShowCreate(false); setForm(blank);
      if (tab === "mine") load("mine"); else setTab("mine");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const join = async (id) => {
    try {
      const { data } = await api.post(`/events/${id}/join`);
      toast.success(data.message);
      const { data: d } = await api.get(`/events/${id}`);
      setDetail(d); setOpenId(id); load(tab);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const act = async (fn, okMsg) => {
    try { const { data } = await fn(); if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(data) : okMsg); refresh(); load(tab);
      if (tab === "mine" && user) api.get(`/events/host/dashboard?kind=${kind}`).then(({ data }) => setDash(data)).catch(() => {}); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 pt-24 pb-16" data-testid={`${kind}-page`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2"><HeroIcon className="text-brand" /><h1 className="font-serif text-4xl tracking-tight">{label}</h1></div>
          <p className="text-sec text-sm mt-2 max-w-xl">
            {isMeal
              ? "Families host neighbours for a home-cooked meal at a minimal charge in your local currency. Guests show interest; hosts invite matching guests. Reserve with the 15% platform fee (via UPI), pay the host 85% directly."
              : "Host any gathering — birthdays, house warmings, weddings, anniversaries, ceremonies, festivals, parties, Kirtans, Satsang, reunions, investor & startup meets. Set your topic, occupancy and ticket price in real money. Reserve with the 15% platform fee via UPI; the host keeps 85%."}
          </p>
        </div>
        {user && <button data-testid="create-listing-btn" onClick={() => setShowCreate(true)} className="btn-primary rounded-full h-10 px-4 text-sm flex items-center gap-1.5"><Plus size={16} /> {isMeal ? "Host a meal" : "Host an event"}</button>}
      </div>

      {/* Tabs */}
      {user && (
        <div className="inline-flex gap-1 p-1 rounded-full bg-white/5 border border-white/10 mt-5" data-testid="board-tabs">
          <button data-testid="tab-discover" onClick={() => setTab("discover")} className={`h-8 px-4 rounded-full text-sm ${tab === "discover" ? "bg-brand text-black font-medium" : "text-sec"}`}>Discover</button>
          <button data-testid="tab-mine" onClick={() => setTab("mine")} className={`h-8 px-4 rounded-full text-sm ${tab === "mine" ? "bg-brand text-black font-medium" : "text-sec"}`}>Hosting</button>
        </div>
      )}

      {tab === "mine" && dash && <HostDashboard dash={dash} isMeal={isMeal} />}

      {tab === "discover" && coords && (
        <div className="flex items-center gap-1.5 text-xs text-brand mt-4" data-testid="nearest-note"><MapPin size={13} /> Showing {isMeal ? "meals" : "events"} nearest to you</div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="listing-list">
        {list.length === 0 && <p className="text-sec text-sm">{tab === "mine" ? `You're not hosting any ${isMeal ? "meals" : "events"} yet.` : "Nothing here yet — be the first to host!"}</p>}
        {list.map((e) => (
          <button key={e.id} data-testid={`listing-${e.id}`} onClick={() => setOpenId(e.id)} className="rud-card rounded-2xl overflow-hidden text-left hover:-translate-y-1 hover:border-brand/50 transition-all">
            {e.cover && <div className="h-36 w-full overflow-hidden"><img src={imgSrc(e.cover)} alt={e.title} className="w-full h-full object-cover" data-testid={`listing-cover-${e.id}`} /></div>}
            <div className="p-5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-brand flex items-center gap-1">{isMeal ? <Utensils size={12} /> : <Users size={12} />} {e.type}</span>
              <span className="text-xs text-sec">{e.status === "cancelled" ? "Cancelled" : `${e.seats_left} left`}</span>
            </div>
            <div className="font-serif text-xl mt-2 leading-tight">{e.title}</div>
            {isMeal && e.menu && <div className="text-xs text-sec mt-1 line-clamp-2">🍲 {e.menu}</div>}
            {!isMeal && e.artists && <div className="text-xs text-sec mt-1 line-clamp-1">🎤 {e.artists}</div>}
            {!isMeal && !e.artists && e.activities && <div className="text-xs text-sec mt-1 line-clamp-1">✨ {e.activities}</div>}
            <div className="text-xs text-sec mt-1 flex items-center gap-1"><MapPin size={11} /> {e.address || "Nearby"} · {e.when || "TBA"}{typeof e.distance_km === "number" ? <span className="text-brand ml-1">· ~{e.distance_km} km away</span> : null}</div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-brand">{e.is_free ? "Free" : `${money(e.symbol, e.price_per_person)} / person`}</span>
              <span className="text-xs text-sec">by {e.host_name}{e.host_reviews ? ` · ${e.host_rating}★` : ""}</span>
            </div>
            {e.top_reviews && e.top_reviews.length > 0 && (
              <div className="mt-2 border-t border-white/5 pt-2 flex items-start gap-1.5" data-testid={`listing-review-${e.id}`}>
                <Quote size={11} className="text-brand shrink-0 mt-0.5" />
                <p className="text-[11px] text-sec italic line-clamp-2">{e.top_reviews[0].comment} <span className="not-italic">— {e.top_reviews[0].reviewer_name}</span></p>
              </div>
            )}
            </div>
          </button>
        ))}
      </div>

      {showCreate && (
        <CreateModal isMeal={isMeal} types={types} form={form} setForm={setForm} currencyList={currencyList} onClose={() => setShowCreate(false)} onSubmit={create} busy={busy} />
      )}

      {openId && detail && (
        <DetailModal
          kind={kind} isMeal={isMeal} detail={detail} user={user} openId={openId}
          onClose={() => setOpenId(null)} act={act} join={join} refresh={() => { refresh(); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3" data-testid={testid}>
      <div className="text-sec flex items-center gap-1 text-[11px] uppercase tracking-wide">{icon} {label}</div>
      <div className="text-2xl font-serif mt-1">{value}</div>
      {sub && <div className="text-[10px] text-sec">{sub}</div>}
    </div>
  );
}

function HostDashboard({ dash, isMeal }) {
  const trend = dash.rating_trend || [];
  return (
    <div className="rud-card rounded-2xl p-5 mt-6" data-testid="host-dashboard">
      <div className="flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-brand" /><h3 className="font-serif text-xl">Host dashboard</h3></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={<Wallet size={14} />} testid="dash-earned" label="Earned (85%)" value={money(dash.symbol, dash.earned)} />
        <Stat icon={<CalendarDays size={14} />} testid="dash-upcoming" label="Upcoming seats" value={dash.upcoming_seats} />
        <Stat icon={<Star size={14} />} testid="dash-rating" label="Host rating" value={dash.host_reviews ? `${dash.host_rating}★` : "—"} sub={dash.host_reviews ? `${dash.host_reviews} reviews` : "no reviews yet"} />
        <Stat icon={<Check size={14} />} testid="dash-completed" label="Completed" value={dash.completed} />
      </div>
      {dash.standing && (dash.standing.note || dash.standing.banned) && (
        <div className="text-xs text-red-400 mt-3 rounded-lg bg-red-500/10 border border-red-500/30 p-3" data-testid="dash-suspended">
          <div className="flex items-center gap-1 font-medium"><Ban size={13} /> {dash.standing.banned ? "Blocked from hosting & attending" : "Temporarily paused"}</div>
          <p className="mt-1">{dash.standing.note}</p>
          {(dash.standing.host_strikes > 0 || dash.standing.guest_strikes > 0) && !dash.standing.banned &&
            <p className="mt-1 text-sec">Strikes — hosting: {dash.standing.host_strikes}/3 · attending: {dash.standing.guest_strikes}/3. A 3rd block removes you from both.</p>}
        </div>
      )}
      {dash.upcoming.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-sec mb-2">Upcoming {isMeal ? "meals" : "events"}</div>
          {dash.upcoming.map((u) => (
            <div key={u.id} className="flex items-center justify-between text-sm py-1.5 border-t border-white/5" data-testid={`dash-upcoming-${u.id}`}>
              <span>{u.title} <span className="text-xs text-sec">· {u.when || "TBA"}</span></span>
              <span className="text-xs text-sec">{u.seats_filled}/{u.capacity} seats{u.pending ? ` · ${u.pending} pending` : ""}</span>
            </div>
          ))}
        </div>
      )}
      {trend.length > 1 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-sec mb-2">Rating over time</div>
          <div className="flex items-end gap-1 h-16" data-testid="rating-trend">
            {trend.slice(-20).map((t, i) => (
              <div key={i} title={`avg ${t.avg}★`} className="flex-1 bg-brand/70 rounded-t" style={{ height: `${Math.max(6, (t.avg / 5) * 100)}%` }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CreateModal({ isMeal, types, form, setForm, currencyList, onClose, onSubmit, busy }) {
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef(null);
  const addPhoto = async () => {
    const f = photoRef.current?.files?.[0];
    if (!f) return;
    if ((form.images || []).length >= 6) { toast.error("Up to 6 photos."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/events/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, images: [...(prev.images || []), data.url] }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (photoRef.current) photoRef.current.value = ""; }
  };
  const removePhoto = (u) => setForm((prev) => ({ ...prev, images: (prev.images || []).filter((x) => x !== u) }));
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="rud-card rounded-2xl p-6 w-full max-w-lg max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-listing-modal">
        <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-2xl">{isMeal ? "Host a meal" : "Host an event"}</h3><button onClick={onClose}><X size={18} /></button></div>
        <div className="grid grid-cols-2 gap-2">
          {isMeal ? (
            <select data-testid="listing-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rud-input rounded-lg px-2 py-2 text-sm">{types.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          ) : (
            <>
              <input data-testid="listing-type" list="event-type-suggestions" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Event type (type any)" className="rud-input rounded-lg px-3 py-2 text-sm" />
              <datalist id="event-type-suggestions">{types.map((t) => <option key={t} value={t} />)}</datalist>
            </>
          )}
          <input data-testid="listing-when" value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} placeholder={isMeal ? "e.g. Tomorrow 8pm" : "e.g. Sat 7pm"} className="rud-input rounded-lg px-3 py-2 text-sm" />
        </div>
        {!isMeal && <p className="text-[11px] text-sec mt-1">Type any event — Marriage, House Warming, Birthday, Satsang, Startup Meet… or pick a suggestion.</p>}
        <input data-testid="listing-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={isMeal ? "Meal title (e.g. Home-style Gujarati Thali)" : "Event title (e.g. Sunrise Yoga in the Park)"} className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />
        {isMeal && <textarea data-testid="listing-menu" value={form.menu} onChange={(e) => setForm({ ...form, menu: e.target.value })} rows={2} placeholder="Tomorrow's menu (e.g. Dal, rice, 2 sabzi, roti, salad, sweet)" className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />}
        {!isMeal && <input data-testid="listing-artists" value={form.artists} onChange={(e) => setForm({ ...form, artists: e.target.value })} placeholder="Artists / performers / speakers (optional)" className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />}
        {!isMeal && <textarea data-testid="listing-activities" value={form.activities} onChange={(e) => setForm({ ...form, activities: e.target.value })} rows={2} placeholder="Activities & timings (e.g. 7pm Kirtan · 8pm dinner · 9pm Satsang)" className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />}
        <textarea data-testid="listing-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder={isMeal ? "About the meal & your home" : "What's this about? Who should come?"} className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />
        <input data-testid="listing-included" value={form.whats_included} onChange={(e) => setForm({ ...form, whats_included: e.target.value })} placeholder="What's included (food, seating, tea…)" className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />
        <textarea data-testid="listing-instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={2} placeholder="Instructions & notes for guests (parking, entry gate, what to bring, house rules…)" className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input data-testid="listing-attire" value={form.attire} onChange={(e) => setForm({ ...form, attire: e.target.value })} placeholder="Attire / dress code (optional)" className="rud-input rounded-lg px-3 py-2 text-sm" />
          <input data-testid="listing-agegroup" value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} placeholder="Age group (e.g. All ages, 18+)" className="rud-input rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="flex flex-wrap gap-2 mt-2" data-testid="listing-prefs">
          {[["couples_only", "Couples only"], ["pet_friendly", "Pet friendly"], ["kids_welcome", "Kids & infants welcome"]].map(([k, lbl]) => (
            <button key={k} type="button" data-testid={`toggle-${k}`} onClick={() => setForm({ ...form, [k]: !form[k] })}
              className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${form[k] ? "bg-brand text-black border-brand" : "border-white/20 text-sec"}`}>
              {form[k] ? "✓ " : ""}{lbl}
            </button>
          ))}
        </div>
        <div className="mt-2">
          <label className="text-xs text-sec flex items-center gap-1"><ImagePlus size={12} /> {isMeal ? "Photos of your dishes" : "Event photos"} (make it irresistible)</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(form.images || []).map((u) => (
              <div key={u} className="relative w-16 h-16 rounded-lg overflow-hidden" data-testid="listing-photo-thumb">
                <img src={imgSrc(u)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(u)} className="absolute top-0 right-0 bg-black/70 rounded-bl px-1"><X size={11} /></button>
              </div>
            ))}
            <label data-testid="listing-photo-add" className="w-16 h-16 rounded-lg border border-dashed border-white/25 grid place-items-center cursor-pointer hover:border-brand/60">
              {uploading ? <Loader2 size={16} className="animate-spin text-brand" /> : <ImagePlus size={16} className="text-sec" />}
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={addPhoto} data-testid="listing-photo-input" />
            </label>
          </div>
        </div>
        <input data-testid="listing-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address / area" className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-2" />
        <div className="mt-2">
          <label className="text-xs text-sec flex items-center gap-1"><CalendarClock size={12} /> Start date & time (for the 9-hour cancellation window)</label>
          <input data-testid="listing-startat" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-1" />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div><label className="text-xs text-sec">{isMeal ? "Price / plate (real money)" : "Ticket price (real money, min 1)"}</label>
            <div className="flex gap-1">
              <select data-testid="listing-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="rud-input rounded-lg px-2 py-2 text-sm w-24">
                {(currencyList.length ? currencyList : [{ code: form.currency, symbol: "" }]).map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
              </select>
              <input data-testid="listing-price" type="number" min={isMeal ? 0 : 1} value={form.price_per_person} onChange={(e) => setForm({ ...form, price_per_person: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div><label className="text-xs text-sec">Occupancy (seats)</label><input data-testid="listing-capacity" type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="rud-input w-full rounded-lg px-3 py-2 text-sm" /></div>
        </div>
        <div className="mt-2">
          <label className="text-xs text-sec">Your UPI ID (to receive your 85% from guests) — optional</label>
          <input data-testid="listing-host-upi" value={form.host_upi} onChange={(e) => setForm({ ...form, host_upi: e.target.value })} placeholder="e.g. yourname@okhdfcbank" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
        </div>
        <p className="text-[11px] text-sec mt-2">{FEE_NOTE} {isMeal ? "Guests show interest → you invite matching ones. They pay the 15% platform fee via UPI to reserve; you collect the 85% directly." : "Guests pay the 15% platform fee via UPI to reserve their seat; you collect the 85% directly."} Cancel free up to 9h before; after that the 15% reserving amount is forfeited unless verified emergency proof is uploaded.</p>
        <p className="text-[11px] text-sec mt-1" data-testid="reputation-policy">⭐ Ratings matter: average below 2★ pauses that role for 6 months, below 3★ for 2 months. After 3 such blocks you're removed from both hosting and attending. Applies to hosts and guests alike.</p>
        <button data-testid="listing-create-submit" disabled={busy} onClick={onSubmit} className="btn-primary w-full h-10 rounded-lg text-sm mt-3 flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : (isMeal ? "Publish meal" : "Publish event")}</button>
      </div>
    </div>
  );
}

function DetailModal({ kind, isMeal, detail, user, openId, onClose, act, join, refresh }) {
  const e = detail.listing;
  const my = detail.my_booking;
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const shareInvite = async () => {
    const url = `${window.location.origin}/e/${e.id}`;
    if (navigator.share) { try { await navigator.share({ title: e.title, text: `You're invited: ${e.title}`, url }); return; } catch (_) {} }
    try { await navigator.clipboard.writeText(url); toast.success("Invite link copied — share it with friends!"); }
    catch { toast.error("Couldn't copy — the link is " + url); }
  };
  const sendBroadcast = () => {
    if (!msg.trim()) { toast.error("Write a message first."); return; }
    act(() => api.post(`/events/${openId}/broadcast`, { message: msg.trim() }), (d) => `Message sent to ${d.sent} guest(s) 🌿`);
    setMsg("");
  };

  const cancel = (bid) => act(() => api.post(`/events/${openId}/cancel/${bid}`), (d) => d.message);
  const hostCancel = () => act(() => api.post(`/events/${openId}/host-cancel`), (d) => `Cancelled — ${d.guests_affected} guest(s) affected; ${money(d.symbol, d.guests_refunded)} to be refunded.`);
  const approve = (bid, decision) => act(() => api.post(`/events/${openId}/approve/${bid}?decision=${decision}`), decision === "approve" ? "Guest invited — they'll pay the 15% to reserve." : "Request declined.");
  const confirmPay = (bid) => act(() => api.post(`/events/${openId}/confirm-payment/${bid}`), (d) => d.message);
  const attended = (bid) => act(() => api.post(`/events/${openId}/attended/${bid}`), (d) => `Checked in — ${money(d.symbol, d.host_received)} to you (collect directly).`);
  const noshow = (bid) => act(() => api.post(`/events/${openId}/noshow/${bid}`), (d) => `Marked no-show — platform kept ${money(d.symbol, d.platform_kept)}.`);
  const review = (bid, rating) => act(() => api.post(`/events/${openId}/review`, { booking_id: bid, rating, comment: "" }), "Thanks for your review!");

  const uploadProof = async (bid) => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a photo or PDF of your proof."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("reason", reason);
      const { data } = await api.post(`/events/${openId}/emergency/${bid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.verdict.verified) toast.success(data.message);
      else toast.error(data.message);
      refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const pending = detail.bookings.filter((b) => b.status === "pending");
  const activeGuests = detail.bookings.filter((b) => b.status === "booked");
  const doneGuests = detail.bookings.filter((b) => ["attended", "no_show"].includes(b.status));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="rud-card rounded-2xl p-6 w-full max-w-lg max-h-[88vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()} data-testid="listing-detail">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-widest text-brand">{e.type}</span>
            <h3 className="font-serif text-2xl">{e.title}</h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button data-testid="share-invite-btn" onClick={shareInvite} title="Share invite" className="btn-ghost rounded-full h-8 px-3 flex items-center gap-1.5 text-xs text-brand"><Share2 size={13} /> Share invite</button>
            <button onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div className="text-xs text-sec mt-1 flex items-center gap-1 flex-wrap">
          <MapPin size={11} /> {e.address || "Nearby"} · {e.when || "TBA"} · by {e.host_name}{e.host_reviews ? ` · ${e.host_rating}★ (${e.host_reviews})` : ""}
        </div>
        {e.images && e.images.length > 0 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="listing-gallery">
            {e.images.map((u, i) => <img key={i} src={imgSrc(u)} alt="" className="h-28 rounded-lg object-cover shrink-0" />)}
          </div>
        )}
        {e.status === "cancelled" && <div className="mt-3 text-sm text-red-400 flex items-center gap-1" data-testid="listing-cancelled"><Ban size={14} /> This {isMeal ? "meal" : "event"} was cancelled by the host.</div>}
        {isMeal && e.menu && <p className="text-sm mt-3" data-testid="listing-menu-view"><b className="text-brand">Menu:</b> {e.menu}</p>}
        {!isMeal && e.artists && <p className="text-sm mt-3" data-testid="listing-artists-view"><b className="text-brand">Line-up:</b> {e.artists}</p>}
        {!isMeal && e.activities && <p className="text-sm mt-2" data-testid="listing-activities-view"><b className="text-brand">Activities:</b> {e.activities}</p>}
        {e.description && <p className="text-sm mt-2">{e.description}</p>}
        {e.whats_included && <p className="text-xs text-sec mt-2"><b>Included:</b> {e.whats_included}</p>}

        {/* Full details */}
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-testid="listing-details">
          <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2"><span className="text-sec">Guests</span><div className="text-[var(--rud-text)]">{e.capacity} max · {e.seats_left} seats left</div></div>
          <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2"><span className="text-sec">Location</span><div className="text-[var(--rud-text)]">{e.address || "Shared on booking"}</div></div>
          {e.when && <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2"><span className="text-sec">When</span><div className="text-[var(--rud-text)]">{e.when}</div></div>}
          {e.attire && <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2"><span className="text-sec">Attire</span><div className="text-[var(--rud-text)]">{e.attire}</div></div>}
          {e.age_group && <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2"><span className="text-sec">Age group</span><div className="text-[var(--rud-text)]">{e.age_group}</div></div>}
        </div>
        {(e.couples_only || e.pet_friendly || e.kids_welcome) && (
          <div className="flex flex-wrap gap-2 mt-2" data-testid="listing-tags">
            {e.couples_only && <span className="text-xs rounded-full bg-brand/15 text-brand px-2.5 py-1">💑 Couples only</span>}
            {e.pet_friendly && <span className="text-xs rounded-full bg-brand/15 text-brand px-2.5 py-1">🐾 Pet friendly</span>}
            {e.kids_welcome && <span className="text-xs rounded-full bg-brand/15 text-brand px-2.5 py-1">🧒 Kids & infants welcome</span>}
          </div>
        )}
        {e.instructions && <div className="mt-3 rounded-lg bg-white/5 border border-white/10 p-3" data-testid="listing-instructions-view"><div className="text-[11px] uppercase tracking-widest text-brand mb-1">Instructions & notes</div><p className="text-sm whitespace-pre-line">{e.instructions}</p></div>}

        <div className="flex items-center justify-between mt-3 text-sm">
          <div>
            <span className="text-brand">{e.is_free ? "Free" : `${money(e.symbol, e.price_per_person)} / person`}</span>
            {!e.is_free && <span className="block text-[11px] text-sec">Reserve with 15% ({money(e.symbol, e.platform_fee)}) · host gets 85% ({money(e.symbol, e.host_amount)})</span>}
          </div>
          <span className="text-sec">{e.seats_left} seats left</span>
        </div>

        {/* Guest actions */}
        {!e.is_host && user && e.status === "open" && (!my || ["declined", "cancelled", "cancelled_late", "no_show", "host_cancelled", "cancelled_emergency"].includes(my.status)) && e.seats_left > 0 && (
          <button data-testid="join-btn" onClick={() => join(openId)} className="btn-primary w-full h-11 rounded-lg text-sm mt-4">
            {isMeal ? "I'm interested" : (e.is_free ? "Join · Free" : `Reserve · pay 15% (${money(e.symbol, e.platform_fee)})`)}
          </button>
        )}

        {my && my.status === "pending" && (
          <div className="mt-4 rounded-lg bg-white/5 border border-white/10 p-3" data-testid="my-pending">
            <p className="text-sm text-brand flex items-center gap-1"><Clock size={14} /> Interest sent — the host will invite matching guests. If invited, pay the 15% platform fee via UPI to confirm your seat; the host collects the 85% directly.</p>
            <button data-testid="withdraw-btn" onClick={() => cancel(my.id)} className="text-xs text-sec hover:text-red-400 mt-2">Withdraw interest</button>
          </div>
        )}

        {my && my.status === "awaiting_payment" && my.payment && (
          <PaymentPanel my={my} isMeal={isMeal} onPaid={() => confirmPay(my.id)} onCancel={() => cancel(my.id)} />
        )}

        {my && my.status === "booked" && (
          <div className="mt-4 rounded-lg bg-brand/10 border border-brand/30 p-3 space-y-2" data-testid="my-booked">
            <p className="text-sm text-brand">Reserved ✓ 15% ({money(my.symbol, my.deposit)}) paid to My Kind. Pay the host the remaining 85% ({money(my.symbol, my.balance)}) directly{isMeal ? " after the meal" : ""}.</p>
            <button data-testid="cancel-btn" onClick={() => cancel(my.id)} className="text-xs rounded-full border border-red-500/40 text-red-400 px-3 py-1.5">Cancel my booking</button>
            {!e.is_free && (
              <div className="border-t border-white/10 pt-2">
                <p className="text-[11px] text-sec flex items-center gap-1"><ShieldAlert size={12} /> Emergency? Upload proof (medical note, etc.) — our AI verifies it to refund your 15% reserving amount.</p>
                <input data-testid="emergency-reason" value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Reason (e.g. hospitalised)" className="rud-input w-full rounded-lg px-3 py-1.5 text-xs mt-2" />
                <input data-testid="emergency-file" ref={fileRef} type="file" accept="image/*,application/pdf" className="text-xs mt-2 text-sec" />
                <button data-testid="emergency-submit" disabled={uploading} onClick={() => uploadProof(my.id)} className="btn-ghost rounded-lg text-xs px-3 py-1.5 mt-2 flex items-center gap-1 disabled:opacity-60">{uploading ? <Loader2 size={12} className="animate-spin" /> : <ShieldAlert size={12} />} Submit emergency proof</button>
              </div>
            )}
          </div>
        )}

        {my && ["cancelled_late", "no_show"].includes(my.status) && !e.is_free && (
          <div className="mt-4 rounded-lg bg-white/5 border border-white/10 p-3" data-testid="reclaim-block">
            <p className="text-sm text-red-400">You forfeited the 15% reserving amount ({money(my.symbol, my.deposit)}).</p>
            <p className="text-[11px] text-sec mt-1 flex items-center gap-1"><ShieldAlert size={12} /> Had a genuine emergency? Upload proof to reclaim it.</p>
            <input data-testid="reclaim-reason" value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="Reason" className="rud-input w-full rounded-lg px-3 py-1.5 text-xs mt-2" />
            <input data-testid="reclaim-file" ref={fileRef} type="file" accept="image/*,application/pdf" className="text-xs mt-2 text-sec" />
            <button data-testid="reclaim-submit" disabled={uploading} onClick={() => uploadProof(my.id)} className="btn-ghost rounded-lg text-xs px-3 py-1.5 mt-2 flex items-center gap-1 disabled:opacity-60">{uploading ? <Loader2 size={12} className="animate-spin" /> : <ShieldAlert size={12} />} Upload proof & reclaim</button>
          </div>
        )}

        {my && my.status === "attended" && (
          <div className="mt-4" data-testid="guest-review">
            <div className="text-sm mb-1">Rate your host</div>
            <Stars testidPrefix="host-star" onRate={(n) => review(my.id, n)} />
          </div>
        )}
        {my && my.status === "cancelled_emergency" && <p className="mt-4 text-sm text-green-400" data-testid="emergency-refunded">Emergency verified — your 15% will be refunded to your UPI 🙏</p>}

        {/* Host controls */}
        {e.is_host && e.status === "open" && (
          <div className="mt-5 border-t border-rud pt-3 space-y-4" data-testid="host-panel">
            {isMeal && (
              <div data-testid="pending-requests">
                <div className="text-sm font-medium mb-2">Join requests ({pending.length})</div>
                {pending.length === 0 && <p className="text-sec text-xs">No requests yet.</p>}
                {pending.map((b) => (
                  <div key={b.id} className="flex items-start justify-between gap-2 py-2 text-sm border-t border-white/5" data-testid={`pending-${b.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{b.guest_name}</span>
                        {b.match?.recommended && <span data-testid={`recommended-${b.id}`} className="text-[9px] uppercase tracking-wide bg-brand/20 text-brand rounded-full px-1.5 py-0.5 flex items-center gap-0.5"><Sparkles size={9} /> Recommended</span>}
                      </div>
                      {b.match && <div className="text-[11px] text-sec mt-0.5">{b.match.reasons.join(" · ")}</div>}
                    </div>
                    <span className="flex gap-1 shrink-0">
                      <button data-testid={`approve-${b.id}`} onClick={() => approve(b.id, "approve")} className="text-xs rounded-full border border-green-500/50 text-green-400 px-2 py-1 flex items-center gap-1"><Check size={12} /> Invite</button>
                      <button data-testid={`decline-${b.id}`} onClick={() => approve(b.id, "decline")} className="text-xs rounded-full border border-red-500/50 text-red-400 px-2 py-1 flex items-center gap-1"><X size={12} /> Decline</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div data-testid="active-guests">
              <div className="text-sm font-medium mb-2">Confirmed guests ({activeGuests.length})</div>
              {activeGuests.length === 0 && <p className="text-sec text-xs">No confirmed guests yet.</p>}
              {activeGuests.map((b) => (
                <div key={b.id} className="flex items-center justify-between py-1.5 text-sm">
                  <span>{b.guest_name}</span>
                  <span className="flex gap-1">
                    <button data-testid={`attended-${b.id}`} onClick={() => attended(b.id)} className="text-xs rounded-full border border-green-500/50 text-green-400 px-2 py-1 flex items-center gap-1"><Check size={12} /> Arrived</button>
                    <button data-testid={`noshow-${b.id}`} onClick={() => noshow(b.id)} className="text-xs rounded-full border border-red-500/50 text-red-400 px-2 py-1 flex items-center gap-1"><UserX size={12} /> No-show</button>
                  </span>
                </div>
              ))}
            </div>
            {doneGuests.length > 0 && (
              <div data-testid="done-guests">
                <div className="text-sm font-medium mb-2">Rate your guests</div>
                {doneGuests.map((b) => (
                  <div key={b.id} className="flex items-center justify-between py-1.5 text-sm">
                    <span>{b.guest_name} <span className="text-xs text-sec">· {b.status}</span></span>
                    <Stars testidPrefix={`guest-star-${b.id}`} onRate={(n) => review(b.id, n)} size={16} />
                  </div>
                ))}
              </div>
            )}
            <div data-testid="broadcast-box" className="border-t border-white/10 pt-3">
              <div className="text-sm font-medium mb-2 flex items-center gap-1.5"><Send size={13} className="text-brand" /> Message all guests</div>
              <textarea data-testid="broadcast-input" value={msg} onChange={(ev) => setMsg(ev.target.value)} rows={2}
                placeholder={`Add a note for everyone coming (e.g. parking, timing, what to bring)…`}
                className="rud-input w-full rounded-lg px-3 py-2 text-sm" maxLength={600} />
              <button data-testid="broadcast-send" onClick={sendBroadcast}
                className="btn-primary rounded-full h-9 px-4 text-xs mt-2 flex items-center gap-1.5">
                <Send size={13} /> Send to {activeGuests.length} guest{activeGuests.length === 1 ? "" : "s"}
              </button>
            </div>
            <button data-testid="host-cancel-btn" onClick={hostCancel} className="text-xs rounded-full border border-red-500/40 text-red-400 px-3 py-1.5 flex items-center gap-1"><Ban size={12} /> Cancel this {isMeal ? "meal" : "event"} (guests' 15% refunded)</button>
          </div>
        )}

        {detail.reviews.length > 0 && (
          <div className="mt-5 border-t border-rud pt-3" data-testid="reviews">
            <div className="text-xs uppercase tracking-widest text-sec mb-2">Reviews</div>
            {detail.reviews.map((r) => (
              <div key={r.id} className="text-sm py-1"><b>{r.reviewer_name}</b> <span className="text-[10px] uppercase text-sec">({r.reviewer_role})</span> <Stars value={r.rating} size={12} /> {r.comment && <span className="text-sec">— {r.comment}</span>}</div>
            ))}
          </div>
        )}

        {(e.is_host || my) && e.status === "open" && <EventChat eventId={e.id} />}
      </div>
    </div>
  );
}


function PaymentPanel({ my, isMeal, onPaid, onCancel }) {
  const p = my.payment;
  const [paying, setPaying] = useState(false);
  const doPaid = async () => { setPaying(true); try { await onPaid(); } finally { setPaying(false); } };
  return (
    <div className="mt-4 rounded-xl bg-brand/5 border border-brand/30 p-4 space-y-3" data-testid="payment-panel">
      <div className="flex items-center gap-2 text-brand"><Wallet size={16} /><span className="font-medium text-sm">Reserve your seat — pay the 15% platform fee via UPI</span></div>

      <div className="rounded-lg bg-white/5 border border-white/10 p-3">
        <div className="text-[11px] uppercase tracking-widest text-sec mb-1">My Kind platform fee (15%)</div>
        <div className="text-2xl font-serif text-brand">{p.symbol}{Number(p.platform_fee).toLocaleString()}</div>
        <div className="text-[11px] text-sec mt-0.5">Pay to <b className="text-[var(--rud-text)]">{p.platform_upi}</b> ({p.platform_payee}) ≈ ₹{Number(p.platform_fee_inr).toLocaleString()}</div>
        {p.platform_qr && <img src={p.platform_qr} alt="UPI QR" className="w-40 h-40 rounded-lg bg-white p-1 mt-2" data-testid="platform-upi-qr" />}
        {p.platform_uri && <a href={p.platform_uri} data-testid="platform-upi-link" className="inline-block text-xs btn-primary rounded-full px-4 py-2 mt-2">Open UPI app to pay 15%</a>}
      </div>

      <div className="rounded-lg bg-white/5 border border-white/10 p-3">
        <div className="text-[11px] uppercase tracking-widest text-sec mb-1">To the host (85%)</div>
        <div className="text-lg font-serif">{p.symbol}{Number(p.host_amount).toLocaleString()}</div>
        {p.host_upi
          ? <>
              <div className="text-[11px] text-sec mt-0.5">Pay the host at <b className="text-[var(--rud-text)]">{p.host_upi}</b> {isMeal ? "after the meal" : "directly"}.</div>
              {p.host_qr && <img src={p.host_qr} alt="Host UPI QR" className="w-32 h-32 rounded-lg bg-white p-1 mt-2" data-testid="host-upi-qr" />}
              {p.host_uri && <a href={p.host_uri} data-testid="host-upi-link" className="inline-block text-xs btn-ghost rounded-full px-4 py-2 mt-2">Open UPI app to pay host</a>}
            </>
          : <div className="text-[11px] text-sec mt-0.5">Settle the 85% with the host directly {isMeal ? "after the meal" : "at the event"}.</div>}
      </div>

      <div className="flex items-center gap-2">
        <button data-testid="confirm-payment-btn" disabled={paying} onClick={doPaid} className="btn-primary flex-1 h-10 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {paying ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} I've paid the 15%
        </button>
        <button data-testid="cancel-awaiting-btn" onClick={onCancel} className="text-xs rounded-full border border-red-500/40 text-red-400 px-3 py-2">Cancel</button>
      </div>
      <p className="text-[10px] text-sec">After you tap “I've paid”, your seat is confirmed. Cancel free up to 9h before start for a 15% refund to your UPI.</p>
    </div>
  );
}


function EventChat({ eventId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [me, setMe] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const load = () => api.get(`/events/${eventId}/chat`).then(({ data }) => { setMsgs(data.messages || []); setMe(data.me); }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [eventId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(`/events/${eventId}/chat`, { text: text.trim() }); setText(""); load(); }
    catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <div className="mt-5 border-t border-rud pt-3" data-testid="event-chat">
      <div className="text-xs uppercase tracking-widest text-sec mb-2 flex items-center gap-1"><MessageCircle size={12} /> Group chat · host &amp; guests</div>
      <div className="max-h-56 overflow-y-auto space-y-2 pr-1" data-testid="event-chat-messages">
        {msgs.length === 0 && <p className="text-xs text-sec">No messages yet — say hello and coordinate the details 🌿</p>}
        {msgs.map((m) => {
          const mine = m.user_id === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${mine ? "bg-brand text-[#050505]" : "rud-card"}`}>
                {!mine && <div className="text-[10px] text-brand mb-0.5">{m.user_name}{m.is_host ? " · Host" : ""}</div>}
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-2">
        <input data-testid="event-chat-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Message the group…"
          className="rud-input flex-1 rounded-full px-4 py-2 text-sm" maxLength={1000} />
        <button data-testid="event-chat-send" disabled={sending} onClick={send}
          className="btn-primary rounded-full h-10 w-10 grid place-items-center disabled:opacity-60">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </div>
    </div>
  );
}
